import { Alert, Button, Dialog, DialogContent, Grid, TextField, Tooltip } from "@mui/material";
import { v4 as uuid } from "uuid";
import React, { useCallback, useState } from "react";
import { useTheme } from "@mui/material/styles";
import DialogTitle from "../../DialogTitle/DialogTitle";
import AutocompleteDropdown from "../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { DocumentTypesApiService } from "../../../services/document-types";
import { DOCUMENT_CATEGORIES } from "../../../utils/constants/constants";
import { DocumentTypeFilter, DocumentTypeRef } from "../../../models/document-types";
import { Period } from "../../../models/documents/period";
import { CustomDate } from "../../../models/custom-date/custom-date";
import CustomDatePicker from "../../CustomDatePicker/CustomDatePicker";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import DialogActions from "../../DialogActions/DialogActions";
import ConfirmationChangesDialog from "../../ConfirmationChangesDialog/ConfirmationChangesDialog";
import LoadingOverlay from "../../LoadingOverlay/LoadingOverlay";
import { SplitEmployeeDocumentDTO } from "../../../models/split-document/split-employee-document/split-employee-document-dto";
import { PublishSplitEmployeeDocumentApiService } from "../../../services/split-document/split-employee-document/publish-split-employee-document-api.service";
import { PublishSplitEmployeeDocumentFilesSTO } from "../../../models/split-document/split-employee-document/publish-split-employee-document-files-sto";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { intlFormatDateShort } from "../../../utils/time-utils";
interface Props {
  handleNotify: (signal?: AbortSignal) => Promise<void>;
  item: SplitEmployeeDocumentDTO;
}

interface State {
  isCCDOpen: boolean;
  open: boolean;
  documentType: DocumentTypeRef | null;
  documentPeriod: Period | null;
  documentDate: CustomDate | null;
  error: string;
  rePublishConfirmed: boolean;
  loading: boolean;
}

const PublishSplitEmployeeDocument = ({ handleNotify, item }: Props) => {
  const theme = useTheme();

  const [state, setState] = useState<State>({
    isCCDOpen: false,
    open: false,
    documentType: null,
    documentPeriod: null,
    documentDate: null,
    error: "",
    rePublishConfirmed: false,
    loading: false,
  });

  const handleSave = useCallback(async () => {
    console.log(handleNotify);
    setState((state) => ({
      ...state,
      loading: true,
    }));
    if (state.documentType) {
      try {
        const response = await new PublishSplitEmployeeDocumentApiService().saveItems([
          {
            id: uuid(),
            documentTypeId: state.documentType.id,
            documentDate: state.documentType.dateInsteadPeriod ? state.documentDate : null,
            documentPeriod: !state.documentType.dateInsteadPeriod ? state.documentPeriod : null,
            splitEmployeeDocumentId: item.id,
          } as PublishSplitEmployeeDocumentFilesSTO,
        ]);

        if (response.error) {
          setState((state) => ({
            ...state,
            error: response.error,
          }));
        } else {
          handleNotify && (await handleNotify());
          setState((state) => ({
            ...state,
            error: "",
            open: false,
          }));
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error,
        }));
      } finally {
        setState((state) => ({
          ...state,
          loading: false,
        }));
      }
    }
  }, [state.documentDate, state.documentPeriod, state.documentType, item.id, handleNotify]);

  return (
    <>
      <Grid style={{ display: "flex", gap: "5px", alignItems: "center" }}>
        <Tooltip title={!item.splitOn ? "No split files to publish" : "Publish split files to employees"}>
          <Button
            onClick={() => setState((state) => ({ ...state, open: true }))}
            style={{
              textTransform: "none",
              color: !item.splitOn ? theme.palette.secondary.main : theme.palette.text.primary,
              justifyContent: "flex-start",
            }}
            disabled={!item.splitOn}
          >
            Publish to employees
          </Button>
        </Tooltip>
        {item.publishedOn ? (
          <Tooltip
            title={`Published on ${intlFormatDateShort(CustomDate.ToDate(item.publishedOn)!)} by ${
              item.publishedBy?.name ?? "<UNDEFINED USER>"
            }`}
          >
            <div>
              <FontAwesomeIcon
                icon={faCircleCheck}
                style={{ color: theme.palette.success.dark }}
              />
            </div>
          </Tooltip>
        ) : (
          <></>
        )}
      </Grid>

      <Dialog open={state.open}>
        <DialogTitle
          title="Publish to employees"
          handleClose={() => setState((state) => ({ ...state, open: false }))}
        />
        <LoadingOverlay
          error={state.error}
          loading={state.loading}
        >
          <DialogContent>
            {!item.publishedOn || state.rePublishConfirmed ? (
              <Grid
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                }}
              >
                <Alert severity="info">
                  Please select a document type and date or period that will be used to create the documents to be
                  published
                </Alert>

                <AutocompleteDropdown
                  labelKey="Document type"
                  selectedValue={state.documentType}
                  field={"documentType"}
                  serviceApi={new DocumentTypesApiService()}
                  propsFilter={
                    {
                      categories: [DOCUMENT_CATEGORIES.EMPLOYMENT_DOCUMENTS.id],
                    } as DocumentTypeFilter
                  }
                  handleChange={(field, value) => {
                    setState((state) => ({
                      ...state,
                      documentType: value,
                    }));
                  }}
                />

                {!state.documentType?.dateInsteadPeriod ? (
                  <CustomDatePicker
                    handlePeriodChange={(period) => setState((state) => ({ ...state, documentPeriod: period }))}
                    period={state.documentPeriod}
                  />
                ) : (
                  <DesktopDatePicker
                    value={CustomDate.ToDate(state.documentDate)}
                    inputFormat="dd.MM.yyyy"
                    mask="__.__.____"
                    onChange={(value) =>
                      value &&
                      setState((state) => ({
                        ...state,
                        documentDate: CustomDate.FromDate(value),
                      }))
                    }
                    renderInput={(params) => <TextField {...params} />}
                  />
                )}
              </Grid>
            ) : item.publishedOn && !state.rePublishConfirmed ? (
              <Alert severity="warning">
                Republishing split document files will result in the deletion of previous published files for the same
                original document.
                <br />
                Are you sure you want to publish again ?
              </Alert>
            ) : (
              <></>
            )}
          </DialogContent>
          <DialogActions
            handleCancel={() => setState((state) => ({ ...state, isCCDOpen: true }))}
            handleSave={
              item.publishedOn && !state.rePublishConfirmed
                ? () => setState((state) => ({ ...state, rePublishConfirmed: true }))
                : handleSave
            }
            {...(item.publishedOn &&
              !state.rePublishConfirmed && {
                customSaveName: "Yes proceed with publishing",
              })}
            disabledSaveButton={
              item.publishedOn ? (state.rePublishConfirmed ? !state.documentType : false) : !state.documentType
            }
          />
        </LoadingOverlay>
      </Dialog>
      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState((state) => ({ ...state, isCCDOpen: false }))}
        handleDiscard={() => setState((state) => ({ ...state, open: false, isCCDOpen: false }))}
      />
    </>
  );
};
export default PublishSplitEmployeeDocument;
